import React, { useState } from 'react';
import { ExternalChip } from '../../lib/types';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import ConfirmSummary from '../../components/ConfirmSummary/ConfirmSummary';
import { PortalField } from '../../components/FormField/PortalField';
import Button from '../../components/Button/Button';
import styles from './Chips.module.scss';

interface ClaimChipModalProps {
  open: boolean;
  onClose: () => void;
  chip: ExternalChip | null;
  onClaim: (chip: ExternalChip, keepOwner: boolean) => void;
}

type OwnerChoice = 'keep' | 'clear';
const DEFAULT_OWNER_CHOICE: OwnerChoice = 'clear';

const OWNER_CHOICES: { value: OwnerChoice; title: string; description: string }[] = [
  {
    value: 'keep',
    title: 'Keep existing owner info',
    description: 'Owner details will stay on the chip, but your rescue will be assigned as the current organization.',
  },
  {
    value: 'clear',
    title: 'Clear owner info',
    description:
      'All owner details will be removed, the chip will be added to your Stock, and assigned to your organization.',
  },
];

export default function ClaimChipModal({ open, onClose, chip, onClaim }: ClaimChipModalProps) {
  const { showToast } = useToast();
  const [ownerChoice, setOwnerChoice] = useState<OwnerChoice>(DEFAULT_OWNER_CHOICE);

  if (!chip) return null;

  const hasOwnerInfo = !!chip.ownerName;

  function handleConfirm() {
    onClaim(chip!, ownerChoice === 'keep');
    showToast(`Chip ${chip!.id} added to your organization`);
    setOwnerChoice(DEFAULT_OWNER_CHOICE);
    onClose();
  }

  function handleClose() {
    setOwnerChoice(DEFAULT_OWNER_CHOICE);
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add to My Organization</ModalTitle>
      <ModalSubtitle>
        {chip.organization
          ? `This will transfer the chip from ${chip.organization} to your organization.`
          : 'This will transfer the chip to your organization.'}
        {chip.petName && (
          <> {chip.ownerName ? 'Pet information will be preserved.' : 'Pet information will be cleared.'}</>
        )}
      </ModalSubtitle>

      <ConfirmSummary
        sections={[
          {
            title: 'Chip',
            fields: [
              { label: 'ID', value: chip.id },
              { label: 'Pet', value: chip.petName },
              { label: 'Current Owner', value: chip.ownerName || 'No owner' },
            ],
          },
        ]}
      />

      {hasOwnerInfo && (
        <PortalField label="Owner Information" fullWidth className={styles.ownerChoiceField}>
          <div className={styles.ownerChoiceGroup} role="radiogroup" aria-label="Owner information">
            {OWNER_CHOICES.map((option) => {
              const active = ownerChoice === option.value;
              return (
                <label
                  key={option.value}
                  className={`${styles.ownerChoiceCard} ${active ? styles.ownerChoiceCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="ownerChoice"
                    value={option.value}
                    checked={active}
                    onChange={() => setOwnerChoice(option.value)}
                    className={styles.ownerChoiceInput}
                  />
                  <span className={styles.ownerChoiceRadio} aria-hidden="true" />
                  <span className={styles.ownerChoiceContent}>
                    <span className={styles.ownerChoiceTitle}>{option.title}</span>
                    <span className={styles.ownerChoiceDescription}>{option.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </PortalField>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm}>
          Confirm
        </Button>
      </ModalActions>
    </Modal>
  );
}
